import { Droppable } from "@hello-pangea/dnd";
import { useTranslate } from "ra-core";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal, PipelineStage } from "../types";
import { DealCard } from "./DealCard";
import { CountUp } from "../misc/CountUp";
import { formatCompactCurrency } from "../misc/formatCurrency";

export const DealColumn = ({
  stage,
  deals,
  biggestTotal = 0,
}: {
  stage: PipelineStage;
  deals: Deal[];
  /** total of the richest column: the money bar is drawn against it */
  biggestTotal?: number;
}) => {
  const totalAmount = deals.reduce((sum, deal) => sum + (deal.amount ?? 0), 0);
  const { currency } = useConfigurationContext();
  const translate = useTranslate();
  const stageLabel = stage.name;
  const share =
    biggestTotal > 0 ? Math.round((totalAmount / biggestTotal) * 100) : 0;
  return (
    // min-w: on a phone the board scrolls sideways inside itself
    <div className="flex-1 min-w-36 pb-8">
      <div className="flex flex-col gap-1 px-0.5">
        <h3 className="vv-heading leading-tight truncate">{stageLabel}</h3>
        <div className="flex items-baseline justify-between gap-2">
          <CountUp
            value={totalAmount}
            label={translate("crm.board.stage_total", { stage: stageLabel })}
            format={(amount) => formatCompactCurrency(amount, currency)}
            className="text-sm font-semibold text-primary-ink tabular-nums"
          />
          <span className="text-xs text-muted-foreground tabular-nums">
            {deals.length}
          </span>
        </div>
        <div className="h-1 rounded-full bg-muted overflow-hidden" aria-hidden>
          <div
            className="h-full rounded-full bg-chart-1 vv-grow"
            style={{ width: `${share}%` }}
            data-share={share}
          />
        </div>
      </div>
      <Droppable droppableId={String(stage.id)}>
        {(droppableProvided, snapshot) => (
          <div
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
            className={`flex flex-col rounded-xl mt-3 gap-2 min-h-16 transition-colors ${
              snapshot.isDraggingOver ? "bg-muted/70" : ""
            }`}
          >
            {deals.map((deal, index) => (
              <DealCard key={deal.id} deal={deal} index={index} />
            ))}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};
